import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { ShieldAlert } from 'lucide-react'
import { useAuthStore } from '@/stores/auth-store'
import { useMyPermissions } from '@/hooks/use-settings'
import { useEnabledModules } from '@/hooks/use-integrations'
import { MAIN_NAV } from './nav-config'
import { FEATURES } from '@/lib/features'
import { Loading, EmptyState } from './feedback'
import type { Role } from '@/types/api'

/**
 * Yêu cầu đã đăng nhập; nếu chưa → /login.
 * `roles`      — vai trò gốc được vào (lớp cũ, bám chỗ backend trả 403).
 * `permission` — khoá quyền động (`module.view`): thiếu quyền thì hiện màn
 *                "không có quyền" thay vì redirect (redirect về /dashboard có
 *                thể vòng lặp khi chính dashboard cũng bị cấm).
 * Server luôn kiểm tra lại — đây chỉ là lớp trải nghiệm.
 */
/** Module sở hữu đường dẫn hiện tại — suy từ MAIN_NAV (tiền tố dài nhất khớp). */
function moduleCuaDuongDan(pathname: string): string | undefined {
  const khop = MAIN_NAV.filter((n) => n.module && (pathname === n.to || pathname.startsWith(n.to + '/')))
    .sort((a, b) => b.to.length - a.to.length)
  return khop[0]?.module
}

export function ProtectedRoute({
  children, roles, permission,
}: { children: ReactNode; roles?: Role[]; permission?: string }) {
  const { user, loading } = useAuthStore()
  const location = useLocation()
  const permsQ = useMyPermissions()
  const modules = useEnabledModules()

  if (loading) return <Loading label="Đang tải..." className="h-screen" />
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />

  // Module super admin chưa cấp cho tổ chức: không render, kể cả gõ thẳng địa chỉ.
  const mod = moduleCuaDuongDan(location.pathname)
  if (mod && modules && !modules.has(mod)) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <EmptyState
          icon={ShieldAlert}
          title="Module này chưa được cấp cho tổ chức của bạn"
          description="Liên hệ đơn vị vận hành hệ thống để mở thêm module."
        />
      </div>
    )
  }

  if (permission && FEATURES.ROLES_PERMISSIONS) {
    if (permsQ.isLoading) return <Loading label="Đang tải..." className="h-screen" />
    if (permsQ.data && !permsQ.data.has(permission)) {
      return (
        <div className="flex h-[60vh] items-center justify-center">
          <EmptyState
            icon={ShieldAlert}
            title="Bạn không có quyền vào mục này"
            description="Vai trò của bạn chưa được cấp quyền xem. Liên hệ quản trị viên nếu bạn cần truy cập."
          />
        </div>
      )
    }
  }
  return <>{children}</>
}
